// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import React, { createElement, forwardRef, useCallback } from 'react';

import { useKeyboardFocus } from '../../utils/hooks';

export interface BooleanInputContainerProps {
  checked?: boolean;
  indeterminate?: boolean;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  focused?: boolean;
  hovered?: boolean;
  readOnly?: boolean;
  hasError?: boolean;
  accentColor?: string;
  id?: string;
  name?: string;
  role?: React.HTMLAttributes<HTMLInputElement>['role'];
}

export interface BooleanInputProps {
  className?: string;
  children?: React.FunctionComponent<BooleanInputContainerProps>;
  disabled?: boolean;
  focused?: boolean;
  hovered?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  hasError?: boolean;
  accentColor?: string;
  value?: string | number | boolean;
  indeterminate?: boolean;
  id?: string;
  name?: string;
  role?: React.HTMLAttributes<HTMLInputElement>['role'];
}

const defaultBooleanContainer: React.FunctionComponent<BooleanInputContainerProps> = ({
  children,
}) => <>{children}</>;

const BooleanInput: React.ForwardRefRenderFunction<HTMLInputElement, BooleanInputProps> = (
  {
    className,
    children: BooleanContainer = defaultBooleanContainer,
    disabled = false,
    focused,
    hovered,
    onBlur,
    onChange,
    onFocus,
    readOnly = false,
    value,
    indeterminate = false,
    hasError = false,
    accentColor,
    role,
    ...restProps
  },
  ref
) => {
  const [isFocused, , setIsFocused] = useKeyboardFocus(false);
  const handleFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (e) => {
      setIsFocused(true);
      if (typeof onFocus === 'function') {
        onFocus(e);
      }
    },
    [onFocus, setIsFocused]
  );

  const handleBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(
    (e) => {
      setIsFocused(false);
      if (typeof onBlur === 'function') {
        onBlur(e);
      }
    },
    // setIsFocused is static
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onBlur]
  );

  const handleChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      if (!e.currentTarget.disabled && !e.currentTarget.readOnly) {
        onChange(e, e.currentTarget.checked);
      }
    },
    [onChange]
  );

  return createElement(
    BooleanContainer,
    {
      className,
      checked: !!value,
      indeterminate,
      disabled,
      focused: focused == null ? isFocused : focused,
      hovered,
      readOnly,
      hasError,
      accentColor,
      role,
    },
    <input
      {...restProps}
      checked={!!value}
      disabled={disabled}
      onBlur={handleBlur}
      onChange={onChange && handleChange}
      onFocus={handleFocus}
      readOnly={readOnly}
      ref={ref}
      type="checkbox"
      value={1}
      aria-checked={indeterminate ? 'mixed' : !!value}
      role={role}
    />
  );
};

export default forwardRef(BooleanInput);
